import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import type { JobVolumePoint } from "@shared-core/data/api-types";
import { niceAxisMax } from "@shared-core/domain/insights-monthly-revenue";
import {
  firstBrandhubSharePct,
  latestBrandhubSharePct,
  projectJobVolume,
  type JobVolumeDatum,
} from "@shared-core/domain/insights-sales-mix";

// Real-job counts booked each month, split by source — rep-booked vs
// self-serve BrandHub (BH360) — stacked bars (left axis) with the BrandHub
// share line (right axis). Server composes (fct_chargeable_work's is_brandhub
// flag, real jobs only), client only derives the share % and renders.

const REP_COLOR = "var(--color-chart-1)";
const BRANDHUB_COLOR = "var(--color-chart-3)";
const SHARE_COLOR = "var(--color-chart-2)";

export interface JobVolumeChartProps {
  points: JobVolumePoint[];
}

export function JobVolumeChart({ points }: JobVolumeChartProps) {
  const data = projectJobVolume(points);

  if (data.length === 0) {
    return (
      <div className="border-border bg-surface text-muted-foreground rounded-lg border p-12 text-center text-sm">
        No job data for this period yet.
      </div>
    );
  }

  const countMax = niceAxisMax(Math.max(0, ...data.map((d) => d.totalCount)));
  const latestShare = latestBrandhubSharePct(data);
  const firstShare = firstBrandhubSharePct(data);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-foreground text-base font-semibold">
            Jobs booked — rep vs BrandHub — monthly
          </h2>
          <p className="text-muted-foreground text-sm">
            Whole company · real jobs only · trailing {data.length} months
          </p>
        </div>
        <div className="border-border bg-surface rounded-lg border px-3 py-2 text-right">
          <div className="text-muted-foreground text-xs font-medium">
            BrandHub share
          </div>
          <div className="text-foreground font-mono text-xl font-semibold tabular-nums">
            {latestShare.toFixed(1)}%
          </div>
          <div className="text-muted-foreground mt-0.5 text-xs">
            was {firstShare.toFixed(1)}%
          </div>
        </div>
      </div>

      <div className="border-border bg-surface rounded-lg border p-4">
        <ChartLegend />
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart
            data={data}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          >
            <CartesianGrid stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }}
              axisLine={{ stroke: "var(--color-border)" }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={16}
            />
            <YAxis
              yAxisId="count"
              domain={[0, countMax]}
              allowDataOverflow
              tickCount={5}
              tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
              width={40}
            />
            <YAxis
              yAxisId="share"
              orientation="right"
              domain={[0, 100]}
              tickCount={5}
              tickFormatter={(v: number) => `${v}%`}
              tick={{ fontSize: 10, fill: SHARE_COLOR }}
              axisLine={false}
              tickLine={false}
              width={40}
            />
            <Tooltip
              cursor={{ fill: "var(--color-muted)", opacity: 0.3 }}
              content={JobVolumeTooltip as never}
            />
            <Bar
              yAxisId="count"
              dataKey="repCount"
              name="Rep-booked"
              stackId="jobs"
              fill={REP_COLOR}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
              fillOpacity={0.95}
            />
            <Bar
              yAxisId="count"
              dataKey="brandhubCount"
              name="BrandHub"
              stackId="jobs"
              fill={BRANDHUB_COLOR}
              isAnimationActive={false}
              fillOpacity={0.95}
            />
            <Line
              yAxisId="share"
              dataKey="brandhubSharePct"
              name="BrandHub share"
              stroke={SHARE_COLOR}
              strokeWidth={2.4}
              dot={{ r: 2.6, fill: SHARE_COLOR, strokeWidth: 0 }}
              connectNulls
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

function ChartLegend() {
  return (
    <div className="text-muted-foreground mb-3 flex flex-wrap items-center gap-4 text-xs">
      <LegendSwatch color={REP_COLOR} label="Rep-booked" />
      <LegendSwatch color={BRANDHUB_COLOR} label="BrandHub" />
      <LegendLine color={SHARE_COLOR} label="BrandHub share (right axis)" />
    </div>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-3 w-3 rounded-sm"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

function LegendLine({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-0 w-4"
        style={{ borderTop: `3px solid ${color}` }}
      />
      {label}
    </span>
  );
}

function JobVolumeTooltip(props: TooltipContentProps<number, string>) {
  if (!props.active || !props.payload || props.payload.length === 0) {
    return null;
  }
  const datum = props.payload[0]?.payload as JobVolumeDatum | undefined;
  if (!datum) return null;
  return (
    <div
      className="border-border bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow-lg"
      role="tooltip"
    >
      <div className="mb-1 font-semibold">{datum.label}</div>
      <TooltipRow
        color={REP_COLOR}
        label="Rep-booked"
        value={String(datum.repCount)}
      />
      <TooltipRow
        color={BRANDHUB_COLOR}
        label="BrandHub"
        value={String(datum.brandhubCount)}
      />
      <div className="text-muted-foreground mt-0.5">
        {datum.totalCount} jobs total · {datum.brandhubSharePct.toFixed(1)}%
        BrandHub
      </div>
    </div>
  );
}

function TooltipRow({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-block h-2 w-2 rounded-sm"
          style={{ backgroundColor: color }}
        />
        {label}
      </span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}
